import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { isEqual } from "lodash";
import { useStoreActions, useStoreState } from "@models/RootStore";
import { useSetModbusUnitRegisterValue } from "@hooks/useModbusDevicesApi";
import { IModbusDeviceRow } from "../../../../Modbus/types";
import { getModbusRegisterKey } from "../../../../Modbus/ModbusUnitControl";
import { formatValue } from "../helpers";
import { withLatestOperationalData } from "@utils/latestOperationalData";

const EMPTY_OBJECT: Record<string, any> = {};

const getModbusUniversalMappedPoints = (rows: IModbusDeviceRow[]) => {
  const mapped: Record<string, any> = {};
  rows.forEach((row) => {
    if (row.property) {
      const key = getModbusRegisterKey(row);
      if (key) mapped[row.property] = { row, key };
    }
  });
  return mapped;
};

const getModbusOperationalValue = (
  propertyKey: string,
  mappedPoints: Record<string, any>,
  operationalData: Record<string, any>
) => {
  const point = mappedPoints[propertyKey];
  if (!point?.key) return null;
  return operationalData[point.key] ?? null;
};

export function useModbusUniversalControls(entry: any) {
  const { id, rows = [], operationalData: fetchedOperationalData = EMPTY_OBJECT } = entry;
  // Start from what arrived over the websocket since the entry was fetched, not just the entry itself
  const entryOperationalData = useMemo(() => withLatestOperationalData(id, fetchedOperationalData), [id, fetchedOperationalData]);
  const entityTemplate = entry.entityTemplate ?? entry.paramTemplateData?.entityTemplate ?? false;

  const types = useStoreState((s) => s.types);
  const { temperatureScale = 0 } = useStoreState((s) => s.users.me);
  const { addMessage: addErrorMessage } = useStoreActions((a) => a.errorMessage);
  const { addWebSocketListener, removeWebSocketListener } = useStoreActions((a: any) => a);

  const togglePending = useRef(false);
  const revertToggle = useRef<(() => void) | null>(null);

  const { mutate: setValue } = useSetModbusUnitRegisterValue({
    onSuccess: () => { togglePending.current = false; },
    onError: (err: any) => {
      revertToggle.current?.();
      togglePending.current = false;
      addErrorMessage({ message: err?.message || "Failed to update value" });
    },
  });

  const { operationModesExtended } = types;

  // ── Live operational data via WebSocket ───────────────────────────────────

  const [dynamicOperationalData, setDynamicOperationalData] = useState<Record<string, any>>(entryOperationalData);

  useEffect(() => {
    if (!isEqual(dynamicOperationalData, entryOperationalData)) {
      setDynamicOperationalData(entryOperationalData);
    }
  }, [entryOperationalData]);

  useEffect(() => {
    const handle = (message: any) => {
      if (message.name !== "UPDATE_MODBUS_UNIT_DATA" || message.data?.unitId !== id) return;
      const newData = message.data?.operationalData;
      if (!newData) return;
      setDynamicOperationalData((prev) => {
        // The device pushes full snapshots; a partial message (an optimistic value) only updates its own points
        const next = message.data?.partial ? { ...prev, ...newData } : newData;
        return isEqual(prev, next) ? prev : next;
      });
    };
    addWebSocketListener(handle);
    return () => removeWebSocketListener(handle);
  }, [id]);

  // ── Mapped points + readiness ─────────────────────────────────────────────

  const universalMappedPoints = useMemo(() => getModbusUniversalMappedPoints(rows), [rows]);

  const isUniversalUnit = entityTemplate === true;
  const hasUniversalControls = Object.keys(universalMappedPoints).length > 0;
  const isReady = isUniversalUnit && hasUniversalControls;

  // ── Key derivation ────────────────────────────────────────────────────────

  const onOffKey = universalMappedPoints["operationStatus"] ? "operationStatus" : null;
  const modeKey = universalMappedPoints["operationMode"] ? "operationMode" : null;
  const setpointKey = universalMappedPoints["setpoint"] ? "setpoint" : null;
  const roomTempKey = universalMappedPoints["roomTemperature"] ? "roomTemperature" : null;

  // ── Current values ────────────────────────────────────────────────────────

  const currentOnOff = onOffKey ? getModbusOperationalValue(onOffKey, universalMappedPoints, dynamicOperationalData) : null;
  const currentSetpoint = setpointKey ? getModbusOperationalValue(setpointKey, universalMappedPoints, dynamicOperationalData) : null;
  const currentRoomTemp = roomTempKey ? getModbusOperationalValue(roomTempKey, universalMappedPoints, dynamicOperationalData) : null;

  const currentModeRaw = modeKey ? getModbusOperationalValue(modeKey, universalMappedPoints, dynamicOperationalData) : null;
  const modePoint = modeKey ? universalMappedPoints[modeKey] : null;
  const modeConfig = modePoint
    ? { customEnum: modePoint.row?.customEnum, propertyMapping: modePoint.row?.propertyMapping }
    : null;
  const currentMode =
    currentModeRaw !== null && modeConfig
      ? formatValue(currentModeRaw, modeConfig, operationModesExtended)
      : currentModeRaw;

  // ── Mode text resolution ──────────────────────────────────────────────────

  let currentModeIndex = currentModeRaw;
  let modeText = "";

  if (typeof currentMode === "string") {
    const foundIndex = operationModesExtended?.findIndex?.(
      (m: string) => m?.toUpperCase() === currentMode.toUpperCase()
    );
    if (foundIndex !== undefined && foundIndex !== -1) {
      currentModeIndex = foundIndex;
      modeText = operationModesExtended[foundIndex];
    } else {
      modeText = currentMode.toUpperCase();
    }
  } else if (typeof currentModeIndex === "number") {
    modeText = operationModesExtended?.[currentModeIndex] || "";
  }

  // ── Derived display flags ─────────────────────────────────────────────────

  const isOn = currentOnOff === 1 || currentOnOff === "1" || currentOnOff === true;
  const isHeatOn = isOn && modeText === "HEAT";
  const isCoolOn = isOn && modeText === "COOL";
  const isFanOn = isOn && modeText === "FAN";

  const isStatusHeat = isHeatOn;
  const isStatusCool = isCoolOn;
  const showSetpoint = isFanOn ? false : isHeatOn || isCoolOn || !!currentSetpoint;

  // ── Temperature unit ──────────────────────────────────────────────────────

  const tempUnit = types.temperatureScale[temperatureScale as number] === "celsius" ? "°C" : "°F";

  // ── Handler ───────────────────────────────────────────────────────────────

  const handleOnOffToggle = useCallback(
    (e: any) => {
      e.stopPropagation();
      e.preventDefault();
      if (!onOffKey || togglePending.current) return;
      const point = universalMappedPoints[onOffKey];
      if (!point?.row) return;

      const newValue = isOn ? 0 : 1;

      // Store revert function before optimistic update so onError can undo it.
      revertToggle.current = () =>
        setDynamicOperationalData((prev) => ({ ...prev, [point.key]: isOn ? 1 : 0 }));

      // Optimistic update — toggle flips immediately without waiting for WebSocket.
      setDynamicOperationalData((prev) => ({ ...prev, [point.key]: newValue }));

      togglePending.current = true;
      setValue({
        modbusUnitId: id,
        data: {
          register: {
            type: point.row.registerType,
            address: point.row.registerAddress,
            ...(point.row.bitNumber != null ? { bitNumber: point.row.bitNumber } : {}),
          },
          value: newValue,
        },
      });
    },
    [isOn, onOffKey, universalMappedPoints, id, setValue]
  );

  // ── Public surface ────────────────────────────────────────────────────────

  return {
    isReady,
    isLoading: false,
    isOn,
    isStatusHeat,
    isStatusCool,
    showSetpoint,
    modeText,
    currentMode,
    currentSetpoint,
    currentRoomTemp,
    tempUnit,
    onOffKey,
    setpointKey,
    roomTempKey,
    isFanOn,
    handleOnOffToggle,
  };
}
